import { Component, computed, inject, input } from '@angular/core';
import {
  AuthUserDto,
  InspectionDetailDto,
  InspectionPdfDetailDto,
  InspectionPdfItemDto,
  InspectionPdfService,
} from '@artemis-software/wr-api';
import { firstValueFrom, throwError, timeout } from 'rxjs';
import { InspectionPdfDownloadService } from '@services/inspection-pdf-download.service';
import { tryWithLoadingSignal } from '@/utils/async-utils';
import { catchError } from 'rxjs/operators';
import { FormControl, FormGroup } from '@angular/forms';
import { NbDialogService, NbToastrService } from '@nebular/theme';
import { PdfShareLinkDialogComponent } from '@components/dialog/pdf-share-link-dialog/pdf-share-link-dialog.component';
import { DialogService } from '@services/dialog-service';
import { loader } from '@/utils/signals/loader';
import { InspectionContext } from '@pages/inspection-detail-page-v2/inspection.context';
import InspectionPdfTypeEnum = InspectionPdfDetailDto.InspectionPdfTypeEnum;

@Component({
  standalone: false,
  selector: 'wr-inspection-pdf-entry',
  templateUrl: './inspection-pdf-entry.component.html',
  styleUrls: ['./inspection-pdf-entry.component.scss'],
})
export class InspectionPdfEntryComponent {

  private readonly inspectionPdfService = inject(InspectionPdfService);
  private readonly inspectionPdfDownloadService = inject(InspectionPdfDownloadService);
  private readonly nbDialogService = inject(NbDialogService);
  private readonly dialogService = inject(DialogService);
  private readonly context = inject(InspectionContext);
  private readonly toastr = inject(NbToastrService);

  protected readonly TenantTypeEnum = AuthUserDto.TenantTypeEnum;
  protected readonly InspectionPdfTypeEnum = InspectionPdfTypeEnum;

  readonly loading = loader();
  readonly canEdit = computed(() => this.inspection().permissions.canEdit);

  label = input.required<string>();
  inspection = input.required<InspectionDetailDto>();
  inspectionPdfType = input.required<InspectionPdfTypeEnum>();

  inspectionPdf = input.required<InspectionPdfItemDto | undefined>();

  filter = new FormGroup({
    contractorId: new FormControl<string | null>(null),
    buildingSections: new FormControl([]),
    excludeFinishedRepairTasks: new FormControl<boolean>(false, { nonNullable: true }),
  });

  async generatePdf() {
    await tryWithLoadingSignal(this.loading, async () => {
      await firstValueFrom(
        this.inspectionPdfService.generatePdf({
          inspectionId: this.inspection().id,
          inspectionPdfType: this.inspectionPdfType(),
          contractorId: this.filter.value.contractorId || undefined,
          buildingSections: this.filter.value.buildingSections ?? [],
          excludeFinishedRepairTasks: this.inspectionPdfType() === InspectionPdfTypeEnum.Shortlist
            && !!this.filter.value.excludeFinishedRepairTasks,
        }).pipe(
          timeout(120000),
          catchError(error => {
            if (error.name === 'TimeoutError') {
              console.error('Request timed out. Please try again.');
            } else {
              console.error('An error occurred:', error.message);
            }
            return throwError(error);
          }),
        ),
      );
      this.context.reloadInspectionPdfs();
    });
  }

  // Laeuft ueber den Download-Endpoint statt die presigned URL, damit ein fehlendes S3-Objekt als
  // 404-Toast endet und nicht als rohes S3-XML im neuen Tab (WR-2497). Der Tab wird synchron im
  // Klick geoeffnet, weil der Browser ein window.open nach dem await als Popup blockt.
  async openPdfLink() {
    const pdf = this.inspectionPdf();
    if (!pdf) return;

    const tab = window.open('', '_blank');
    try {
      await tryWithLoadingSignal(this.loading, async () => {
        const blob = await firstValueFrom(this.inspectionPdfDownloadService.fetchPdf(pdf.id));
        const url = window.URL.createObjectURL(blob);
        if (tab) {
          tab.location.href = url;
        } else {
          window.open(url, '_blank');
        }
      });
    } catch (error) {
      tab?.close();
      throw error;
    }
  }

  async downloadPdf() {
    const pdf = this.inspectionPdf();
    if (!pdf) return;

    const loader = this.loading.add();
    loader.set(true);
    try {
      await firstValueFrom(
        this.inspectionPdfDownloadService.downloadPdf(this.fileName(), pdf.id, this.fileExtension())
      );
    } finally {
      loader.set(false);
      loader.stop();
    }
  }

  readonly fileName = computed(() => {
    const inspection = this.inspection();
    const sectionText = (inspection.building.baseData.sectionText ?? '')
      .replace(/[/]/g, '')
      .replace(/[ ,]/g, '_');
    return `${this.typeText()}_Rundgang_${inspection.continuationNumber}_${sectionText}`;
  });

  // Die Objektsicherheitsliste ist die einzige Nicht-PDF-Ausgabe; der Download-Service haengt
  // sonst pauschal .pdf an und die Datei laesst sich in Excel nicht oeffnen.
  readonly fileExtension = computed(() =>
    this.inspectionPdfType() === InspectionPdfTypeEnum.ObjectSafetyList ? 'xlsx' : 'pdf',
  );

  // Oeffnen und Teilen zeigen das Dokument inline als PDF an (die Share-Seite rendert den Blob
  // fest als application/pdf im iframe) — fuer die Excel-Liste gibt es deshalb nur den Download.
  readonly supportsInlineView = computed(() => this.fileExtension() === 'pdf');

  readonly typeText = computed(() => {
    const labels: Record<string, string> = {
      [InspectionPdfTypeEnum.Shortlist]: 'Shortlist',
      [InspectionPdfTypeEnum.Longlist]: 'Longlist',
      [InspectionPdfTypeEnum.Coversheet]: 'Deckblatt',
      [InspectionPdfTypeEnum.BuildingEquipments]: 'TGAs',
      [InspectionPdfTypeEnum.CertificateSheet]: 'Zertifikat',
      [InspectionPdfTypeEnum.BuildingEquipmentsSchloss]: 'TGAs Spielplatzgeräte',
      [InspectionPdfTypeEnum.CertificateSheetSchloss]: 'Zertifikat Spielplatzgeräte',
      [InspectionPdfTypeEnum.ObjectSafetyReport]: 'Objektsicherheitsbericht',
      [InspectionPdfTypeEnum.Schadstellenplan]: 'Schadstellenplan',
      [InspectionPdfTypeEnum.ObjectSafetyList]: 'Objektsicherheitsliste',
    };
    return labels[this.inspectionPdfType()] || 'PDF';
  });

  async openPdfShareLink() {
    await firstValueFrom(
      this.nbDialogService.open(PdfShareLinkDialogComponent, {
        context: { inspectionPdfType: this.inspectionPdfType() },
      }).onClose
    );
  }

  async deletePdf() {
    const pdf = this.inspectionPdf();
    if (!pdf) return;

    const confirmed = await this.dialogService.showDeleteDialog(
      `${this.label()} löschen`,
      'Dokument samt allen älteren Versionen dieses Typs (auch dienstleisterspezifischen) '
      + 'und allen Share Links löschen? Bereits versendete Share Links funktionieren danach nicht mehr.',
    );
    if (!confirmed) return;

    await tryWithLoadingSignal(this.loading, async () => {
      await firstValueFrom(this.inspectionPdfService.deleteGenerated(pdf.id));
      this.toastr.success(`${this.label()} wurde gelöscht`, 'Gelöscht');
      this.context.reloadInspectionPdfs();
    });
  }

  getTooltipContent(pdf: InspectionPdfItemDto): string {
    const contractor = pdf.contractorName?.trim() || 'Kein Dienstleister';

    const sections = pdf.buildingSectionNames?.trim() || 'Alle Gebäudeteile';

    const repairTasks = pdf.excludeFinishedRepairTasks
      ? 'Ohne abgeschlossene Reparaturaufträge'
      : 'Alle Reparaturaufträge';

    return `${contractor}   |    ${sections}   |    ${repairTasks}`;
  }
}
